/**
 * YouTube Downloader page — R2 wiring.
 *
 * State machine: Landing → Progress → Complete (or Failed/Canceled).
 * Progress polls /api/youtube/download/:jobId/status every 1500ms.
 * jobId is mirrored to localStorage so a refresh resumes the running
 * job instead of dropping the user back to Landing.
 *
 * Terms gate: on mount we call /api/youtube/status; if termsAcceptedAt
 * is null we show a one-time modal. Submitting it calls /accept-terms
 * and unlocks the form.
 *
 * yt-dlp work is still mocked server-side in R2 — R3 swaps the worker
 * for actual child-process spawning. API shapes won't change.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ChevronLeft,
  Download,
  ExternalLink,
  Search,
  X,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import type { AuthSession } from './auth'
import {
  acceptYoutubeTerms,
  cancelJob,
  createDownload,
  formatBytes,
  formatDuration,
  formatRelative,
  getHistory,
  getJob,
  getYoutubeStatus,
  isTerminal,
  type Format,
  type Quality,
  type YoutubeJob,
} from './youtubeApi'

const LS_JOB_KEY = 'leferi.yt.activeJob'
const POLL_MS = 1500

const QUALITY_OPTIONS: { value: Quality; label: string; badge?: string }[] = [
  // Best uncaps the height — produces 4K AV1 on premium uploads, which
  // means hundreds-of-MB files + slow ffmpeg transcoding + larger S3
  // and proxy egress costs. The "High cost" badge nudges people to
  // pick 1080p unless they actually need the maximum.
  { value: 'best', label: 'Best', badge: 'High cost' },
  { value: '1080p', label: '1080p' },
  { value: '720p', label: '720p' },
  { value: '480p', label: '480p' },
  { value: 'audio', label: 'Audio only' },
]

const FORMAT_OPTIONS: { value: Format; label: string; badge?: string }[] = [
  { value: 'mp4', label: 'MP4' },
  { value: 'mp3', label: 'MP3' },
]

type View =
  | { kind: 'landing' }
  | { kind: 'progress'; jobId: string }
  | { kind: 'complete'; job: YoutubeJob }

export function YoutubeDownloaderPage({
  session: _session,
}: {
  session: AuthSession
}) {
  const [view, setView] = useState<View>({ kind: 'landing' })
  const [termsAcceptedAt, setTermsAcceptedAt] = useState<string | null>(null)
  const [statusLoading, setStatusLoading] = useState(true)
  // Kept around for forward-compat with re-enabling the closed-beta
  // gate later (server can still return 403 beta_only via env). Today
  // the gate is off so this stays false.
  const [betaBlocked, setBetaBlocked] = useState(false)
  const [history, setHistory] = useState<YoutubeJob[] | null>(null)

  // Resume any job that was in flight when the user refreshed.
  useEffect(() => {
    const stored = window.localStorage.getItem(LS_JOB_KEY)
    if (stored) {
      setView({ kind: 'progress', jobId: stored })
    }
    void Promise.all([getYoutubeStatus(), getHistory()])
      .then(([s, h]) => {
        setTermsAcceptedAt(s.termsAcceptedAt)
        setHistory(h)
      })
      .catch((err: unknown) => {
        // /status returns 403 beta_only when the user isn't on the
        // YOUTUBE_ALLOWED_EMAILS allowlist. Show a friendly overlay
        // instead of the form.
        if (err instanceof Error && err.message === 'beta_only') {
          setBetaBlocked(true)
        }
        setHistory([])
      })
      .finally(() => setStatusLoading(false))
  }, [])

  const reloadHistory = useCallback(() => {
    void getHistory()
      .then(setHistory)
      .catch(() => undefined)
  }, [])

  const startJob = useCallback((jobId: string) => {
    window.localStorage.setItem(LS_JOB_KEY, jobId)
    setView({ kind: 'progress', jobId })
  }, [])

  const finishJob = useCallback(
    (job: YoutubeJob) => {
      window.localStorage.removeItem(LS_JOB_KEY)
      setView({ kind: 'complete', job })
      reloadHistory()
    },
    [reloadHistory],
  )

  const cancelView = useCallback(() => {
    window.localStorage.removeItem(LS_JOB_KEY)
    setView({ kind: 'landing' })
    reloadHistory()
  }, [reloadHistory])

  const showTerms = !termsAcceptedAt && !statusLoading && !betaBlocked

  if (betaBlocked) {
    return (
      <div className="pt-s-7">
        <Link
          to="/"
          className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
        >
          <ChevronLeft size={14} strokeWidth={1.5} />
          Apps
        </Link>
        <div className="mt-s-9 border border-line-hairline p-s-9 text-center">
          <p className="lf-eyebrow">▪ Closed beta</p>
          <h1 className="mt-s-3 lf-display-2">Coming soon.</h1>
          <p className="mt-s-4 mx-auto max-w-[480px] lf-body text-fg-2">
            YouTube Downloader는 현재 비공개 베타 단계예요. 안정화 후
            전체 공개될 예정이에요.
          </p>
          <p className="mt-s-2 mx-auto max-w-[480px] lf-caption text-fg-3">
            This tool is in closed beta and isn&apos;t available to you yet.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-s-7">
      <Link
        to="/"
        className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline bg-surface-base px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 focus-visible:outline-none focus-visible:shadow-focus"
      >
        <ChevronLeft size={14} strokeWidth={1.5} />
        Apps
      </Link>

      <div className="mt-s-7">
        <p className="lf-eyebrow">▪ Tool</p>
        <h1 className="mt-s-3 lf-display-2">YouTube downloader.</h1>
        <p className="mt-s-2 max-w-[640px] lf-body-sm text-fg-3">
          Pull a YouTube video as a file for campaign reports, creator
          analysis, or internal backup. Files expire after{' '}
          <span className="lf-num">24h</span>.
        </p>

        {/* Cost + support advisory. Brand blue per spec — blue is the
            "info / advisory" surface in our token system; pink stays
            reserved for primary CTAs + selection. */}
        <aside
          role="note"
          className="mt-s-5 flex max-w-[760px] flex-col gap-s-2 border-l-2 border-brand-blue bg-blue-50 p-s-4"
        >
          <p className="lf-body-sm text-brand-blue">
            ▪ 해당 다운로드 기능은 유료 서비스라서 업무에 꼭 필요한
            다운로드만 부탁드려요.
          </p>
          <p className="lf-caption text-brand-blue/85">
            ▪ 다운로드에 오류가 있거나 문제가 있으시면 AX 관련 부서로
            문의해 주세요.
          </p>
        </aside>
      </div>

      {view.kind === 'landing' ? (
        <LandingView
          disabled={showTerms || statusLoading}
          onCreated={startJob}
        />
      ) : view.kind === 'progress' ? (
        <ProgressView
          jobId={view.jobId}
          onDone={finishJob}
          onCancel={cancelView}
        />
      ) : (
        <CompleteView
          job={view.job}
          onReset={() => setView({ kind: 'landing' })}
        />
      )}

      <HistorySection history={history} />

      {showTerms ? (
        <TermsModal
          onAccept={async () => {
            await acceptYoutubeTerms()
            setTermsAcceptedAt(new Date().toISOString())
          }}
        />
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Landing — URL + chips + submit                                              */
/* -------------------------------------------------------------------------- */

function LandingView({
  disabled,
  onCreated,
}: {
  disabled: boolean
  onCreated: (jobId: string) => void
}) {
  const [url, setUrl] = useState('')
  const [quality, setQuality] = useState<Quality>('best')
  const [format, setFormat] = useState<Format>('mp4')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Audio-only forces MP3 — keep state consistent with the UI rule.
  const effectiveFormat: Format = quality === 'audio' ? 'mp3' : format

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      const { jobId } = await createDownload({
        youtubeUrl: url.trim(),
        quality,
        format: effectiveFormat,
      })
      onCreated(jobId)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(humanizeError(msg))
      setBusy(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-s-7 grid grid-cols-1 gap-s-7 lg:grid-cols-[minmax(0,1fr)_360px]"
    >
      <div className="flex flex-col gap-s-6">
        <Field
          label="YouTube URL"
          hint="Paste a full youtube.com or youtu.be link."
        >
          <div className="flex items-center gap-s-2 rounded-r-1 border border-line-soft bg-surface-base px-s-3 py-s-2 focus-within:border-line-strong focus-within:shadow-focus">
            <Search size={14} strokeWidth={1.5} className="shrink-0 text-fg-3" />
            <input
              type="url"
              inputMode="url"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="min-w-0 flex-1 border-0 bg-transparent lf-body text-fg-1 placeholder:text-fg-3 focus:outline-none"
            />
          </div>
        </Field>

        <Field label="Quality" hint="Higher quality means a bigger file.">
          <ChipGroup
            options={QUALITY_OPTIONS}
            value={quality}
            onChange={setQuality}
          />
        </Field>

        <Field
          label="Format"
          hint={
            quality === 'audio'
              ? 'Audio only is fixed to MP3.'
              : 'MP4 for video, MP3 for audio extraction.'
          }
        >
          <ChipGroup
            options={FORMAT_OPTIONS}
            value={effectiveFormat}
            onChange={setFormat}
            disabled={quality === 'audio'}
          />
        </Field>
      </div>

      <aside className="flex flex-col gap-s-4">
        <button
          type="submit"
          disabled={disabled || busy || !url.trim()}
          className="inline-flex w-full items-center justify-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Download size={16} strokeWidth={1.75} />
          {busy ? 'Starting...' : 'Download'}
        </button>
        <p className="lf-eyebrow flex items-start gap-s-2 text-fg-3">
          <span aria-hidden>▪</span>
          <span>
            Internal use only. Downloaded files expire after{' '}
            <span className="lf-num">24h</span>.
          </span>
        </p>
        {error ? (
          <p className="lf-body-sm text-status-danger">▪ {error}</p>
        ) : null}
      </aside>
    </form>
  )
}

/* -------------------------------------------------------------------------- */
/* Progress — poll status, show meta + bar + cancel                            */
/* -------------------------------------------------------------------------- */

function ProgressView({
  jobId,
  onDone,
  onCancel,
}: {
  jobId: string
  onDone: (job: YoutubeJob) => void
  onCancel: () => void
}) {
  const [job, setJob] = useState<YoutubeJob | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [canceling, setCanceling] = useState(false)
  const stoppedRef = useRef(false)

  useEffect(() => {
    stoppedRef.current = false
    let timer: number | undefined

    const tick = async () => {
      try {
        const next = await getJob(jobId)
        if (stoppedRef.current) return
        setJob(next)
        if (isTerminal(next.status)) {
          if (next.status === 'completed') {
            onDone(next)
          } else if (next.status === 'failed') {
            setError(humanizeError(next.errorMessage || 'unknown'))
          } else if (next.status === 'canceled') {
            onCancel()
          } else if (next.status === 'expired') {
            // Surfaced like complete-but-empty so the user knows the file's gone.
            onDone(next)
          }
          return
        }
        timer = window.setTimeout(tick, POLL_MS)
      } catch (err) {
        if (stoppedRef.current) return
        setError(humanizeError(err instanceof Error ? err.message : String(err)))
      }
    }
    void tick()
    return () => {
      stoppedRef.current = true
      if (timer) window.clearTimeout(timer)
    }
  }, [jobId, onDone, onCancel])

  const handleCancel = async () => {
    if (canceling) return
    setCanceling(true)
    try {
      await cancelJob(jobId)
      onCancel()
    } catch {
      setCanceling(false)
    }
  }

  return (
    <section className="mt-s-7 border border-line-hairline bg-surface-base p-s-7">
      <p className="lf-eyebrow">▪ Downloading</p>
      <div className="mt-s-3 flex items-start gap-s-4">
        <VideoThumb url={job?.thumbnailUrl} title={job?.videoTitle ?? ''} />
        <div className="min-w-0 flex-1">
          <h2 className="lf-h2 text-fg-1">
            {job?.videoTitle?.trim() || 'Preparing the file…'}
          </h2>
          <p className="mt-s-1 lf-body-sm text-fg-3">
            {job?.channelName ? <>{job.channelName} · </> : null}
            <span className="lf-num">
              {job?.durationSec ? formatDuration(job.durationSec) : '—'}
            </span>
          </p>
        </div>
      </div>

      <div className="mt-s-6">
        <div className="flex items-end justify-between">
          <span className="lf-caption text-fg-2">
            {job?.status === 'pending' ? 'Queued' : 'Downloading'}
          </span>
          <span className="lf-num lf-caption text-fg-3">
            {job?.progress ?? 0}%
          </span>
        </div>
        <div className="mt-s-2 h-[6px] w-full bg-surface-soft">
          <div
            className="h-full bg-brand-pink transition-[width] duration-base ease-standard"
            style={{ width: `${Math.min(100, job?.progress ?? 0)}%` }}
          />
        </div>
      </div>

      <div className="mt-s-6 flex items-center justify-between gap-s-4">
        <p className="lf-caption text-fg-3">
          Keep this tab open. You can refresh — the job will resume.
        </p>
        <button
          type="button"
          onClick={handleCancel}
          disabled={canceling}
          className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline px-s-3 py-s-2 lf-body-sm text-fg-2 transition-colors duration-base ease-standard hover:border-line-soft hover:bg-surface-soft hover:text-fg-1 disabled:opacity-50"
        >
          <X size={14} strokeWidth={1.5} />
          {canceling ? 'Cancelling...' : 'Cancel'}
        </button>
      </div>

      {error ? (
        <p className="mt-s-5 lf-body-sm text-status-danger">▪ {error}</p>
      ) : null}
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Complete — show meta + file size + Download/New                             */
/* -------------------------------------------------------------------------- */

function CompleteView({
  job,
  onReset,
}: {
  job: YoutubeJob
  onReset: () => void
}) {
  const expired = job.status === 'expired'

  return (
    <section className="mt-s-7 border border-line-hairline bg-surface-base p-s-7">
      <p className="lf-eyebrow">{expired ? '▪ Expired' : '▪ Ready'}</p>
      <div className="mt-s-3 flex items-start gap-s-4">
        <VideoThumb url={job.thumbnailUrl} title={job.videoTitle ?? ''} />
        <div className="min-w-0 flex-1">
          <h2 className="lf-h2 text-fg-1">
            {job.videoTitle?.trim() || 'Download complete'}
          </h2>
          <p className="mt-s-1 lf-body-sm text-fg-3">
            {job.channelName ? <>{job.channelName} · </> : null}
            <span className="lf-num">{formatDuration(job.durationSec)}</span> ·{' '}
            <span className="lf-num">{formatBytes(job.fileSizeBytes)}</span>
          </p>
        </div>
      </div>

      {expired ? (
        <p className="mt-s-5 lf-body-sm text-fg-2">
          This file expired{' '}
          <span className="lf-num">{job.expiresAt ? formatRelative(job.expiresAt) : ''}</span>
          . Re-download to grab a fresh copy.
        </p>
      ) : null}

      <div className="mt-s-6 flex flex-wrap items-center gap-s-3">
        {!expired ? (
          <a
            href={`/api/youtube/download/${job.jobId}/file`}
            className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-3 lf-h4 text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500"
          >
            <Download size={16} strokeWidth={1.75} />
            Download file
          </a>
        ) : null}
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-s-2 rounded-r-1 border border-line-hairline px-s-3 py-s-2 lf-body-sm text-fg-1 hover:border-line-soft hover:bg-surface-soft"
        >
          New download
        </button>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* History                                                                     */
/* -------------------------------------------------------------------------- */

function HistorySection({ history }: { history: YoutubeJob[] | null }) {
  return (
    <section className="mt-s-9 border-t border-line-hairline pt-s-7">
      <p className="lf-eyebrow">▪ Recent downloads</p>
      {history === null ? (
        <p className="mt-s-5 lf-body-sm text-fg-3">Loading.</p>
      ) : history.length === 0 ? (
        <div className="mt-s-5 border border-line-hairline p-s-9 text-center">
          <p className="lf-h3">No downloads yet.</p>
          <p className="mt-s-2 mx-auto max-w-[420px] lf-body-sm text-fg-3">
            Paste a YouTube URL above and your downloads will show up here for
            the next <span className="lf-num">24h</span>.
          </p>
        </div>
      ) : (
        <ul className="mt-s-5">
          {history.map((j) => (
            <HistoryRow key={j.jobId} job={j} />
          ))}
        </ul>
      )}
    </section>
  )
}

function HistoryRow({ job }: { job: YoutubeJob }) {
  const expired = job.status === 'expired'
  const usable = job.status === 'completed' && !expired
  return (
    <li className="flex items-center gap-s-3 border-b border-line-hairline py-s-3">
      <VideoThumb url={job.thumbnailUrl} title={job.videoTitle ?? ''} size="sm" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-s-2">
          <StatusDot status={job.status} />
          <span className="truncate lf-h4 text-fg-1">
            {job.videoTitle?.trim() || job.youtubeUrl}
          </span>
        </div>
        <p className="mt-s-1 lf-caption text-fg-3">
          <span className="lf-num">{job.quality}</span> ·{' '}
          <span className="lf-num">{job.format.toUpperCase()}</span>
          {job.fileSizeBytes > 0 ? (
            <>
              {' · '}
              <span className="lf-num">{formatBytes(job.fileSizeBytes)}</span>
            </>
          ) : null}
          {' · '}
          <span className="lf-num">{formatRelative(job.createdAt)}</span>
        </p>
      </div>
      {usable ? (
        <a
          href={`/api/youtube/download/${job.jobId}/file`}
          title="Download file"
          className="inline-flex items-center gap-s-1 rounded-r-1 border border-line-hairline px-s-3 py-s-1 lf-caption text-fg-2 transition-colors hover:border-line-soft hover:bg-surface-soft hover:text-fg-1"
        >
          <ExternalLink size={12} strokeWidth={1.5} />
          Get file
        </a>
      ) : (
        <span className="lf-caption text-fg-3">{labelFor(job.status)}</span>
      )}
    </li>
  )
}

/**
 * YouTube thumbnail. Sized in two presets: md (Progress/Complete hero
 * card) and sm (History row). 16:9 box with rounded square corners per
 * BX (radius-0 default; small chip-style 2px corner here so the thumb
 * doesn't look like a hard-edged tile next to body copy).
 *
 * Falls back to a brand-brown solid block when the URL is missing or
 * the img onError fires (e.g. yt-dlp returned a thumbnail that 404s).
 */
function VideoThumb({
  url,
  title,
  size = 'md',
}: {
  url?: string
  title: string
  size?: 'sm' | 'md'
}) {
  const [broken, setBroken] = useState(false)
  const dims =
    size === 'sm'
      ? 'h-[44px] w-[78px]'
      : 'h-[90px] w-[160px] sm:h-[108px] sm:w-[192px]'
  if (!url || broken) {
    return (
      <div
        aria-hidden
        className={`${dims} shrink-0 rounded-r-1 bg-brand-brown/12`}
      />
    )
  }
  return (
    <img
      src={url}
      alt={title ? `${title} thumbnail` : ''}
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={() => setBroken(true)}
      className={`${dims} shrink-0 rounded-r-1 object-cover`}
    />
  )
}

function StatusDot({ status }: { status: YoutubeJob['status'] }) {
  const color =
    status === 'completed'
      ? 'bg-status-success'
      : status === 'failed'
        ? 'bg-status-danger'
        : status === 'canceled' || status === 'expired'
          ? 'bg-fg-3'
          : 'bg-brand-pink'
  return (
    <span
      aria-hidden
      className={`inline-block h-2 w-2 shrink-0 ${color}`}
    />
  )
}

function labelFor(status: YoutubeJob['status']): string {
  switch (status) {
    case 'pending':
      return 'Queued'
    case 'downloading':
      return 'Running'
    case 'failed':
      return 'Failed'
    case 'canceled':
      return 'Canceled'
    case 'expired':
      return 'Expired'
    default:
      return ''
  }
}

/* -------------------------------------------------------------------------- */
/* Terms modal — one-time on first use                                         */
/* -------------------------------------------------------------------------- */

function TermsModal({ onAccept }: { onAccept: () => Promise<void> }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-brown/40 p-s-3 sm:p-s-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="yt-terms-title"
        className="w-full max-w-[520px] bg-surface-base shadow-lf-pop"
      >
        <div className="border-b border-line-hairline px-s-6 py-s-4">
          <p className="lf-eyebrow">▪ Before you start</p>
          <h2 id="yt-terms-title" className="mt-s-1 lf-h3">
            One-time acknowledgement.
          </h2>
        </div>
        {/* Korean block on top (primary audience), English block beneath.
            Per user — bilingual lines reading one-by-one felt choppy;
            grouped blocks read like a real policy excerpt. */}
        <div className="flex flex-col gap-s-5 px-s-6 py-s-5">
          <ol className="flex list-none flex-col gap-s-2 lf-body-sm text-fg-1">
            <li>
              본 도구는 Google API Services User Data Policy에 따라
              레페리의 내부 업무 용도로만 사용됩니다.
            </li>
            <li>다운로드한 콘텐츠는 저작권법을 준수하여 사용해야 합니다.</li>
            <li>외부 배포 또는 상업적 재배포는 금지됩니다.</li>
            <li>
              다운로드 기록은 정책 준수 확인 목적으로만 보관됩니다.
            </li>
          </ol>
          <ol className="flex list-none flex-col gap-s-2 border-t border-line-hairline pt-s-4 lf-caption text-fg-3">
            <li>
              This tool is used solely for Leferi internal business
              purposes, in line with the Google API Services User Data
              Policy.
            </li>
            <li>Downloaded content must be used in compliance with copyright law.</li>
            <li>External distribution or commercial redistribution is prohibited.</li>
            <li>Download records are kept only to verify policy compliance.</li>
          </ol>
        </div>
        {error ? (
          <p className="border-t border-line-hairline px-s-6 py-s-3 lf-body-sm text-status-danger">
            ▪ {humanizeTermsError(error)}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-s-2 border-t border-line-hairline px-s-6 py-s-4">
          <button
            type="button"
            onClick={() => {
              setBusy(true)
              setError(null)
              onAccept()
                .catch((err: unknown) => {
                  setError(err instanceof Error ? err.message : String(err))
                })
                .finally(() => setBusy(false))
            }}
            disabled={busy}
            className="inline-flex items-center gap-s-2 rounded-r-2 bg-brand-pink px-s-5 py-s-2 lf-body-sm text-brand-brown transition-colors duration-base ease-standard hover:bg-pink-500 disabled:opacity-50"
          >
            {busy ? 'Saving...' : 'I understand'}
          </button>
        </div>
      </div>
    </div>
  )
}

function humanizeTermsError(raw: string): string {
  if (raw === 'timeout') {
    return 'Server is taking too long to respond. Check that MONGODB_URI is set on the portal service and try again.'
  }
  if (raw.startsWith('http_')) return `Network error (${raw}).`
  return raw
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function ChipGroup<T extends string>({
  options,
  value,
  onChange,
  disabled,
}: {
  options: { value: T; label: string; badge?: string }[]
  value: T
  onChange: (v: T) => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-s-2">
      {options.map((o) => {
        const selected = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={`inline-flex items-center gap-s-2 rounded-r-1 border px-s-3 py-s-2 lf-body-sm transition-colors duration-base ease-standard focus-visible:outline-none focus-visible:shadow-focus ${
              selected
                ? 'border-brand-pink bg-pink-50 text-fg-1'
                : 'border-line-hairline bg-surface-base text-fg-2 hover:border-line-soft hover:text-fg-1'
            } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          >
            <span>{o.label}</span>
            {o.badge ? (
              // Subtle warning pill — not pink (reserved for primary).
              // Square inner rounding to match the LCMD square motif;
              // amber-ish tone for a "be careful" signal without
              // escalating to red.
              <span className="rounded-r-1 border border-amber-200 bg-amber-50 px-s-2 py-px lf-eyebrow uppercase tracking-wide text-amber-700">
                {o.badge}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-s-2">
      <span className="lf-caption text-fg-2">{label}</span>
      {children}
      {hint ? <span className="lf-caption text-fg-3">{hint}</span> : null}
    </label>
  )
}

function humanizeError(code: string): string {
  switch (code) {
    case 'invalid_url':
      return 'That doesn\'t look like a YouTube URL.'
    case 'invalid_quality':
    case 'invalid_format':
    case 'audio_must_be_mp3':
      return 'Pick a valid quality and format.'
    case 'terms_not_accepted':
      return 'Please accept the terms first.'
    case 'too_many_active':
      return 'You already have 2 downloads in progress. Wait for one to finish.'
    case 'beta_only':
      return 'This tool is in closed beta. Ask the CEO for access.'
    case 'geo_blocked':
      return 'This video isn\'t available from our server\'s region.'
    case 'private':
      return 'This video is private.'
    case 'removed':
      return 'This video was removed.'
    case 'live_stream':
      return 'Live streams can\'t be downloaded yet.'
    case 'too_large':
      return 'File is too large for the current limits.'
    default:
      return code.startsWith('http_') ? `Network error (${code}).` : 'Something went wrong.'
  }
}
